import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Target, MessageCircle, Sparkles, Shield } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Brand palette — identical to the parent Playbook page (teal/cyan/cream).
const NAVY = '#004C54';
const NAVY_DEEP = '#003a40';
const GOLD = '#4FBCC7';
const CREAM = '#F5F3EE';

function OptInForm({ id, onDone, dataTestPrefix }) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/pre-game/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          first_name: firstName.trim(),
          source: 'pre_game_routine',
        }),
      });
      if (r.ok) {
        const body = await r.json();
        onDone(body);
      } else {
        const b = await r.json().catch(() => ({}));
        setErr(b.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setErr('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form id={id} onSubmit={submit} className="space-y-3" data-testid={`${dataTestPrefix}-form`}>
      <Input
        placeholder="First name"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        required
        className="bg-white"
        data-testid={`${dataTestPrefix}-firstname`}
      />
      <Input
        type="email"
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className="bg-white"
        data-testid={`${dataTestPrefix}-email`}
      />
      {err && (
        <p className="text-[13px] text-red-700" data-testid={`${dataTestPrefix}-error`}>{err}</p>
      )}
      <Button
        type="submit"
        disabled={loading || !firstName || !email}
        className="w-full font-bold h-12 text-[14px]"
        style={{ background: NAVY, color: '#fff' }}
        data-testid={`${dataTestPrefix}-submit`}
      >
        {loading ? 'Sending…' : 'Send Me the Free Routine →'}
      </Button>
      <p className="text-[11.5px] text-center" style={{ color: '#666' }}>
        No spam. Unsubscribe anytime. We respect your inbox.
      </p>
    </form>
  );
}

export default function PreGame() {
  const navigate = useNavigate();

  const handleDone = (body) => {
    const pdfUrl = body?.pdf_url || '/pre-game-routine.pdf';
    navigate(`/playbook/thank-you?pdf=${encodeURIComponent(pdfUrl)}`);
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="pre-game-page">
      {/* Top utility bar */}
      <div style={{ background: NAVY, color: 'rgba(255,255,255,0.8)' }} className="text-[12px]">
        <div className="max-w-[1120px] mx-auto px-5 py-2 flex items-center justify-between">
          <Link to="/" className="font-mono-accent uppercase tracking-[0.18em] hover:text-white" data-testid="pre-game-back-home">
            ← apexathleticmind.com
          </Link>
          <a href="#second-cta" className="hidden sm:inline font-semibold hover:text-white" style={{ color: GOLD }} data-testid="pre-game-top-cta-link">
            Get the routine →
          </a>
        </div>
      </div>

      {/* ===== SECTION 1: HERO ===== */}
      <section style={{ background: CREAM }}>
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-14 sm:py-20 grid md:grid-cols-[1.1fr_0.9fr] gap-10 md:gap-14 items-center">
          {/* Left: copy + form */}
          <div>
            <div
              className="font-mono-accent uppercase tracking-[0.26em] text-[10.5px] font-bold mb-5"
              style={{ color: GOLD }}
              data-testid="pre-game-eyebrow"
            >
              Free for athletes
            </div>
            <h1
              className="font-heading text-[2rem] sm:text-[2.8rem] md:text-[3.2rem] font-bold tracking-tight leading-[1.05] mb-5"
              style={{ color: NAVY }}
              data-testid="pre-game-hero-title"
            >
              Confidence isn’t a feeling. <span style={{ color: GOLD }}>It’s a routine.</span>
            </h1>
            <p
              className="text-[16px] sm:text-[18px] leading-relaxed text-[#1a1a1a] mb-6 max-w-[560px]"
              data-testid="pre-game-hero-sub"
            >
              The 60-second pre-game routine I run with state champions. Anchor. Belief. Cue. Three steps,
              one minute, every game — so you walk in feeling like the version of you that already won.
            </p>
            <OptInForm id="pre-game-form-1" onDone={handleDone} dataTestPrefix="pre-game-hero" />
          </div>

          {/* Right: PDF cover preview */}
          <div
            className="relative rounded-2xl overflow-hidden shadow-[0_18px_50px_rgba(0,76,84,0.25)] mx-auto w-full max-w-[360px] aspect-[3/4]"
            style={{ background: CREAM }}
            data-testid="pre-game-cover-preview"
          >
            <div className="absolute inset-x-0 top-0 h-3" style={{ background: GOLD }} />
            <div className="absolute inset-x-0 bottom-0 h-[28%]" style={{ background: NAVY }} />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 pt-10 pb-[30%]">
              <div className="font-mono-accent uppercase tracking-[0.22em] text-[8.5px] font-bold mb-6" style={{ color: NAVY }}>
                Free Playbook · For Athletes
              </div>
              <h3 className="font-heading font-bold leading-[1.05] mb-4 text-[28px]" style={{ color: NAVY }}>
                The 60-Second<br/>Pre-Game Routine
              </h3>
              <p className="text-[11px] leading-relaxed" style={{ color: '#555' }}>
                Anchor. Belief. Cue.<br/>One minute. Every game.<br/>Confidence on demand.
              </p>
              <div className="w-12 h-[2px] mt-5" style={{ background: GOLD }} />
            </div>
            <div className="absolute inset-x-0 bottom-0 px-6 pb-5 text-center">
              <p className="font-heading font-bold text-[13px] text-white mb-1">By Coach Varsay Lewis, M.Ed.</p>
              <p className="text-[10px]" style={{ color: GOLD }}>Founder, Apex Athletic Mind</p>
              <p className="text-[9.5px] mt-1" style={{ color: 'rgba(255,255,255,0.8)' }}>apexathleticmind.com</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 2: WHAT'S INSIDE ===== */}
      <section className="bg-white border-y" style={{ borderColor: 'rgba(0,76,84,0.1)' }}>
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <div className="text-center mb-10">
            <div className="font-mono-accent uppercase tracking-[0.24em] text-[10px] font-bold mb-3" style={{ color: GOLD }}>
              What’s inside
            </div>
            <h2 className="font-heading text-[1.9rem] sm:text-[2.3rem] font-bold tracking-tight leading-tight" style={{ color: NAVY }} data-testid="pre-game-inside-title">
              What’s inside the routine
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-5 max-w-[920px] mx-auto">
            {[
              {
                icon: Target,
                title: 'The ABC Stack',
                blurb: 'Anchor. Belief. Cue. The 60-second routine you run before every game.',
              },
              {
                icon: MessageCircle,
                title: 'Your Cue Word',
                blurb: 'Fillable slots so you build YOUR version. Not a generic script.',
              },
              {
                icon: Sparkles,
                title: 'The 3-Breath Reset',
                blurb: '12 seconds between every play to reset nerves and re-anchor focus.',
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="rounded-xl p-6 border"
                  style={{ background: CREAM, borderColor: 'rgba(0,76,84,0.1)' }}
                  data-testid={`pre-game-inside-${card.title.slice(0, 12).replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div
                    className="w-11 h-11 rounded-md flex items-center justify-center mb-4"
                    style={{ background: NAVY, color: GOLD }}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-heading text-[16px] font-bold mb-1.5" style={{ color: NAVY }}>{card.title}</h3>
                  <p className="text-[13.5px] text-[#3a3a3a] leading-relaxed">{card.blurb}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SECTION 3: WHY IT MATTERS ===== */}
      <section style={{ background: CREAM }}>
        <div className="max-w-[820px] mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <div
            className="rounded-2xl p-7 sm:p-10 border"
            style={{ background: 'white', borderColor: 'rgba(0,76,84,0.1)' }}
          >
            <div className="font-heading text-[44px] leading-none mb-2" style={{ color: GOLD }}>“</div>
            <p
              className="font-heading text-[20px] sm:text-[24px] font-bold leading-snug mb-4"
              style={{ color: NAVY }}
              data-testid="pre-game-pullquote"
            >
              The athletes I see win the most aren’t the most talented. They’re the ones who run the same
              60-second routine before every game — freshman year through state finals.
            </p>
            <p className="text-[13px] font-mono-accent uppercase tracking-[0.18em] font-bold" style={{ color: GOLD }}>
              — Coach Varsay Lewis, M.Ed.
            </p>
          </div>

          <div className="mt-8 text-[15.5px] leading-relaxed text-[#1a1a1a] space-y-4">
            <p>
              Most athletes walk into the gym, don’t feel it yet, and wait. They wait through warm-ups, the
              national anthem, the first whistle. By then they’re already down 4-0.
            </p>
            <p>
              The best athletes don’t wait. They run a 60-second routine that <strong>creates the feeling on
              demand</strong>. Same sequence, same result, every game. It’s a switch they flip, not a mood they
              hope for.
            </p>
            <p style={{ color: NAVY }} className="font-semibold">This playbook gives you that switch.</p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 4: SECOND OPT-IN CTA ===== */}
      <section id="second-cta" style={{ background: NAVY }} className="text-white">
        <div className="max-w-[640px] mx-auto px-5 sm:px-8 py-16 sm:py-20 text-center">
          <div className="font-mono-accent uppercase tracking-[0.24em] text-[10px] font-bold mb-3" style={{ color: GOLD }}>
            Run it before your next game
          </div>
          <h2 className="font-heading text-[1.9rem] sm:text-[2.4rem] font-bold tracking-tight leading-tight mb-5" data-testid="pre-game-cta2-title">
            Get the routine. Walk in ready.
          </h2>
          <p className="text-[14.5px] text-white/75 leading-relaxed mb-7">
            Plus — when you opt in, I’ll send you a 2-week email series with the same mental performance tools
            I use with state champions. Unsubscribe anytime.
          </p>
          <div className="max-w-[420px] mx-auto">
            <OptInForm id="pre-game-form-2" onDone={handleDone} dataTestPrefix="pre-game-cta2" />
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer style={{ background: NAVY_DEEP }} className="text-white/55 text-[12px]">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-2">
          <Link to="/" className="hover:text-white" data-testid="pre-game-footer-home">
            © Apex Athletic Mind
          </Link>
          <div className="flex gap-5 flex-wrap justify-center">
            <Link to="/quiz" className="hover:text-white" data-testid="pre-game-footer-quiz">Mindset Quiz</Link>
            <a href="mailto:volewis@apexathleticmind.com" className="hover:text-white">Contact</a>
            <Link to="/unsubscribe" className="hover:text-white">Unsubscribe</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export { PreGame };
